"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";
import { initPostHog, posthog, identifyUser } from "@/lib/posthog";
import { useGetUserInfo } from "@/services/queries/useGetUserInfo";
import { useSubscription } from "@/hooks/useSubscription";
import { useTrialStatus } from "@/hooks/useTrialStatus";

export function PostHogProvider({ children }: { children: React.ReactNode }) {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const { data: user } = useGetUserInfo();
	const { plan } = useSubscription();
	const { phase, daysRemaining } = useTrialStatus();
	const identifiedRef = useRef<string | null>(null);

	useEffect(() => {
		initPostHog();
	}, []);

	// Identify user when data becomes available (once per user/plan/phase change)
	useEffect(() => {
		if (!user?.id) return;
		const identityKey = `${user.id}-${plan}-${phase}`;
		if (identifiedRef.current === identityKey) return;

		identifyUser({
			id: user.id,
			email: user.email,
			name: user.name,
			plan,
			ocupacao: user.ocupacao,
			subscription_status: phase,
			days_remaining: daysRemaining,
		});
		identifiedRef.current = identityKey;
	}, [user?.id, user?.email, user?.name, user?.ocupacao, plan, phase, daysRemaining]);

	useEffect(() => {
		if (pathname) {
			let url = window.origin + pathname;
			if (searchParams && searchParams.toString()) {
				url = url + `?${searchParams.toString()}`;
			}
			posthog.capture("$pageview", {
				$current_url: url,
			});
		}
	}, [pathname, searchParams]);

	return <>{children}</>;
}

